import type { AdminAssistantData, ResponsePlanView, UatQuestion } from '../types'

/**
 * TEST DATA for the local Response Plan tab preview (Summit State, a
 * fictional customer). Nothing here is a real customer plan, task, note,
 * person or Assistant answer. The preview shows a "Test Data" alert with it.
 */
export const RESPONSE_PLAN_PREVIEW: ResponsePlanView = {
  title: 'Summit State Response Plan',
  customerLabel: 'Summit State',
  goal: 'Member V3 UAT approved by Nov 20',
  updatedLabel: 'Nov 4, 2026',
  lede: 'What we are shipping, what is due, and who owns each step.',
  shippingIntro: 'Each piece lists the concerns from your team\'s feedback that it solves. Open a prototype, click around, and add a note on that card.',
  sections: [
    {
      key: 'timeline',
      kind: 'timeline',
      title: 'Where Member V3 Stands',
      intro: '',
      stats: [
        { value: 'Nov 17', label: 'Every Member V3 item in your UAT, including the new AI Advisor' },
        { value: 'Nov 20', label: 'Your team approves Member V3 UAT' },
        { value: 'Early Dec', label: 'Optional Admin V3 beta for your team' },
      ],
      milestones: [
        { date: 'Nov 3', title: 'Monthly check-in and V3 timeline agreed', state: 'done', text: 'We agreed on the dates below and on weekly check-ins until Member V3 is live.' },
        { date: 'This week', title: 'Your test cases reach Prentus', state: 'next', text: 'Your V3 test cases go into our QA before the next delivery.' },
        { date: 'Nov 17', title: 'Every Member V3 item in your UAT', state: 'later', text: 'Including the new AI Advisor and the new job board.' },
        { date: 'Nov 20', title: 'Member V3 UAT approved', state: 'later', text: 'Your team then sets the go-live date for students.' },
      ],
      fixedTitle: 'Recently Fixed',
      fixed: [
        { text: 'Bulk coach updates retry any member that fails', when: 'Oct 21' },
        { text: 'Resume AI Analyzer works again for Word resumes', when: 'Oct 27' },
      ],
    },
    { key: 'tasks', kind: 'tasks', title: 'Open Tasks', intro: 'Select any task to see the details. Anyone on either team can mark a task done.', link: { label: 'Book Our Weekly Check-In', url: 'https://cal.com/example-test-data' } },
    {
      key: 'member-v3',
      kind: 'card',
      title: 'Member V3',
      intro: '',
      what: 'The new student experience, in your UAT now.',
      chip: 'prog',
      chipText: 'In Your UAT',
      timing: 'All items by Nov 17. Approval by Nov 20.',
      solves: ['Students not knowing what happens after they become a jobseeker', 'Jobseekers stalling, with daily job matches', 'Help at any hour, with an AI Advisor'],
      prototype: null,
      tryIt: '',
    },
    {
      key: 'job-board',
      kind: 'card',
      title: 'The New Job Board',
      intro: '',
      what: 'A new job engine that replaces the search students use today.',
      chip: 'prog',
      chipText: 'Part of Member V3',
      timing: 'In your UAT by Nov 17.',
      solves: ['Searches that come back thin', 'City, state and remote searches returning the wrong jobs'],
      prototype: { label: 'Open the Job Board Prototype', url: 'https://example.com/test-data-prototype' },
      tryIt: 'Click through the prototype, then add a note below.',
    },
    {
      key: 'feedback',
      kind: 'feedback',
      title: 'Your Nov 3 Feedback',
      intro: 'Ideas from our check-in that are now on our roadmap.',
      items: [
        { text: 'Student default tasks that appear as soon as onboarding ends', chip: 'prog', chipText: 'In Progress' },
        { text: 'Release notes for every release, with before and after screenshots', chip: 'plan', chipText: 'Planned' },
      ],
    },
    {
      key: 'also',
      kind: 'also',
      title: 'Also on Our List',
      intro: '',
      items: [
        { text: 'Saved job searches with alerts', chip: 'plan', chipText: 'Planned' },
        { text: 'Automatic round-robin CSA assignment', chip: 'plan', chipText: 'Planned' },
      ],
    },
  ],
  tasks: [
    { id: '7c1d0000-0000-4000-8000-000000000001', key: 'docs', title: 'Send the Member V3 change documentation', owner: 'prentus', ownerName: 'Test Prentus Lead', due: 'Nov 3', detail: 'Covers every change in Member V3 so your team can plan testing.', done: true, doneBy: 'Test Tester', doneBySide: 'customer', doneAt: '2026-11-04T14:10:00.000Z' },
    { id: '7c1d0000-0000-4000-8000-000000000002', key: 'deliver', title: 'Deliver every Member V3 item to your UAT', owner: 'prentus', ownerName: 'Test Prentus Lead', due: 'Nov 17', detail: 'Includes the new job board and the AI Advisor.', done: false, doneBy: null, doneBySide: null, doneAt: null },
    { id: '7c1d0000-0000-4000-8000-000000000003', key: 'testcases', title: 'Send your Member V3 test cases', owner: 'customer', ownerName: 'Test Tester', due: 'This week', detail: 'We load them into our QA.', done: false, doneBy: null, doneBySide: null, doneAt: null },
    { id: '7c1d0000-0000-4000-8000-000000000004', key: 'uat', title: 'Approve Member V3 UAT', owner: 'customer', ownerName: 'Test Tester', due: 'Nov 20', detail: 'Then your team sets the go-live date.', done: false, doneBy: null, doneBySide: null, doneAt: null },
  ],
  notes: [
    {
      id: '7c1d0000-0000-4000-8000-0000000000a1',
      sectionKey: 'member-v3',
      sectionTitle: 'Member V3',
      body: 'Does the career readiness plan show for students who already finished onboarding, or only for new students?',
      authorName: 'Test Tester',
      createdAt: '2026-11-04T15:02:00.000Z',
      replies: [
        {
          id: '7c1d0000-0000-4000-8000-0000000000b1',
          body: 'Yes, by design it shows for every student, not only new ones. A student who already finished onboarding sees the plan the next time they open the dashboard, with the steps they already did marked complete.',
          authorName: 'Prentus Assistant',
          authorSide: 'assistant',
          createdAt: '2026-11-04T15:02:40.000Z',
          corrected: false,
          sources: [{ label: 'Summit State Response Plan: Member V3', href: '#response-plan' }, { label: 'Prentus QA guide: Member onboarding', href: null }],
        },
        { id: '7c1d0000-0000-4000-8000-0000000000b2', body: 'Thanks, that helps. We will test it with an existing student account.', authorName: 'Test Tester', authorSide: 'customer', createdAt: '2026-11-04T15:20:00.000Z', corrected: false, sources: [] },
      ],
    },
    {
      id: '7c1d0000-0000-4000-8000-0000000000a2',
      sectionKey: 'tasks',
      sectionTitle: 'Open Tasks',
      body: 'We will send the test cases on Thursday.',
      authorName: 'Test Tester',
      createdAt: '2026-11-04T16:00:00.000Z',
      replies: [{ id: '7c1d0000-0000-4000-8000-0000000000b3', body: 'Thank you. We will load them the same day.', authorName: 'Test Prentus Lead', authorSide: 'prentus', createdAt: '2026-11-04T16:30:00.000Z', corrected: false, sources: [] }],
    },
  ],
  viewer: { side: 'customer', canNote: true, canReply: true, canModerate: false },
  assistantEnabled: true,
}

/** TEST DATA: Ask a Question entries for the local UAT tab preview. */
export const UAT_QUESTIONS_PREVIEW: UatQuestion[] = [
  {
    id: '6f3c1d2e-0000-4000-8000-0000000000q1',
    body: 'When a student saves a filter set, does it also keep the sort order?',
    authorName: 'Test Tester',
    createdAt: '2026-11-04T13:15:00.000Z',
    replies: [{
      id: '6f3c1d2e-0000-4000-8000-0000000000r1',
      body: 'Yes, by design. A saved filter set keeps the filters and the sort order, so the list looks the same when the student opens it again.',
      authorName: 'Prentus Assistant',
      authorSide: 'assistant',
      createdAt: '2026-11-04T13:15:30.000Z',
      corrected: false,
      sources: [{ label: 'UAT item: Job Board Saved Filters', href: '#uat' }, { label: 'Prentus QA guide: Jobs (member)', href: null }],
    }],
  },
]

/** TEST DATA for the local admin Assistant card. */
export function assistantPreviewAdminData(): AdminAssistantData {
  return {
    enabled: false,
    updatedBy: null,
    updatedAt: null,
    configured: true,
    docCount: 0,
    docsSyncedAt: null,
    runs: [
      {
        id: '7c1d0000-0000-4000-8000-0000000000c1',
        parentType: 'plan_note',
        parentId: '7c1d0000-0000-4000-8000-0000000000a1',
        where: 'Response Plan: Member V3',
        question: RESPONSE_PLAN_PREVIEW.notes[0].body,
        status: 'answered',
        verdict: 'by_design',
        answer: RESPONSE_PLAN_PREVIEW.notes[0].replies[0].body,
        sources: [{ label: 'Summit State Response Plan: Member V3', href: '#response-plan', adminUrl: null }, { label: 'Prentus QA guide: Member onboarding', href: null, adminUrl: 'https://github.com/Prentus/claude-plugin/blob/main/skills/qa/flows/member-onboarding.md' }],
        model: 'test-data',
        error: null,
        replyId: '7c1d0000-0000-4000-8000-0000000000b1',
        replyHidden: false,
        replyCorrected: false,
        markerStatus: 'none',
        createdAt: '2026-11-04T15:02:00.000Z',
      },
      {
        id: '7c1d0000-0000-4000-8000-0000000000c2',
        parentType: 'uat_event',
        parentId: '6f3c1d2e-0000-4000-8000-0000000000b4',
        where: 'UAT: Job Board Saved Filters',
        question: 'The new name shows only after a page reload.',
        status: 'escalated',
        verdict: 'needs_team',
        answer: 'This looks like something the team should check, because the sources say a new name shows right away. The Prentus team will follow up in this thread.',
        sources: [{ label: 'UAT item: Job Board Saved Filters', href: '#uat', adminUrl: null }],
        model: 'test-data',
        error: null,
        replyId: null,
        replyHidden: false,
        replyCorrected: false,
        markerStatus: 'done',
        createdAt: '2026-11-04T15:05:00.000Z',
      },
    ],
  }
}
